/**
 * @file combobox.tsx
 */

"use client";

import * as React from "react";
import { ChevronsUpDownIcon } from "lucide-react";
import { cn } from "visuais/fmt";
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from ".";
import { IconSquareCheckFilled } from "@tabler/icons-react";

/**
 * @returns {JSX.Element} Tipo de item do Combobox.
 * @example
 * ```tsx
 * const item: ComboboxItem = { value: "1", label: "Opção 1" };
 * ```
 */
export interface ComboboxItem<T = string> {
  value: T;
  label: string;
}

/**
 * @returns {JSX.Element} Propriedades do Combobox.
 * @example
 * ```tsx
 * <Combobox
 *   options={[{ value: "1", label: "Opção 1" }]}
 *   value="1"
 *   onChange={(v) => console.log(v)}
 *   placeholder="Selecione..."
 * />
 * ```
 */
export interface ComboboxProps<T = string> {
  options?: ComboboxItem<T>[];
  items?: ComboboxItem<T>[];
  value?: T;
  selectedValue?: T;
  onChange?: (value: T | undefined) => void;
  onSelect?: (value: T | undefined) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  className?: string;
  disabled?: boolean;
  width?: string | number;
  maxHeight?: string | number;
  modal?: boolean;
  trigger?: React.ReactNode;
  listHeader?: React.ReactNode;
  closeOnSelect?: boolean;
}

/**
 * @returns {JSX.Element} Combobox interativo.
 * @example
 * ```tsx
 * // Trigger padrão
 * <Combobox
 *   options={[{ value: "1", label: "Opção 1" }, { value: "2", label: "Opção 2" }]}
 *   value="1"
 *   onChange={(v) => console.log(v)}
 *   placeholder="Selecione uma opção..."
 * />
 *
 * // Trigger customizado
 * <Combobox
 *   options={[{ value: "1", label: "Opção 1" }]}
 *   value="1"
 *   onChange={(v) => console.log(v)}
 *   trigger={<div className="p-2 border rounded">Meu Trigger</div>}
 * />
 * ```
 */
export function Combobox<T = string>({
  options,
  items,
  value,
  selectedValue,
  onChange,
  onSelect,
  placeholder = "Selecione uma Opcão..",
  searchPlaceholder = "Buscar...",
  emptyMessage = "Nada encontrado.",
  className,
  disabled = false,
  width,
  maxHeight = "300px",
  trigger,
  modal = false,
  listHeader,
  closeOnSelect = true
}: ComboboxProps<T>) {
  const actualValue = value !== undefined ? value : selectedValue;
  const actualOnChange = onChange ?? onSelect;

  const [open, setOpen] = React.useState(false);
  const [triggerWidth, setTriggerWidth] = React.useState<string>("auto");
  const [searchValue, setSearchValue] = React.useState("");
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const customTriggerRef = React.useRef<HTMLDivElement>(null);
  const actualOptions = React.useMemo(() => {
    const allOptions = options ?? items ?? [];
    // Filtra opções com valores undefined ou vazios para evitar chaves duplicadas
    return allOptions.filter(
      (option: ComboboxItem<T>) =>
        option.value !== undefined &&
        option.value !== null &&
        String(option.value).trim() !== ""
    );
  }, [options, items]);

  // Filtro customizado para busca mais inteligente
  const filteredOptions = React.useMemo(() => {
    if (!searchValue.trim()) return actualOptions;

    const search = searchValue.toLowerCase().trim();

    return actualOptions.filter((option) => {
      const value = String(option.value).toLowerCase();
      const label = option.label.toLowerCase();

      // Busca exata por valor (código)
      if (value === search) return true;

      // Busca por substring no valor (código)
      if (value.includes(search)) return true;

      // Busca por substring no label (nome/descrição)
      if (label.includes(search)) return true;

      return false;
    });
  }, [actualOptions, searchValue]);

  const selectedLabel = React.useMemo(
    () => actualOptions.find((option) => option.value === actualValue)?.label,
    [actualOptions, actualValue]
  );

  const triggerButton = React.useMemo(() => {
    if (trigger) {
      return trigger;
    }

    return (
      <Button
        ref={triggerRef}
        variant="outline"
        role="combobox"
        aria-expanded={open}
        className={cn(
          "w-full justify-between",
          !selectedLabel && "text-muted-foreground"
        )}
        disabled={disabled}
      >
        <span className="truncate max-w-[80%]">{selectedLabel || placeholder}</span>
        <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>
    );
  }, [trigger, selectedLabel, placeholder, open, disabled]);

  const updateWidth = React.useCallback(() => {
    const element = trigger ? customTriggerRef.current : triggerRef.current;
    if (element) {
      const rect = element.getBoundingClientRect();
      setTriggerWidth(`${rect.width}px`);
    }
  }, [trigger]);

  React.useEffect(() => {
    if (open) {
      updateWidth();
      window.addEventListener("resize", updateWidth);
      return () => window.removeEventListener("resize", updateWidth);
    }
  }, [open, updateWidth]);

  const handleSelect = React.useCallback(
    (selected: string) => {
      // Encontra a opção correspondente pelo valor completo
      const selectedOption = actualOptions.find(
        (option) => `${option.value} ${option.label}` === selected
      );

      if (selectedOption) {
        const newValue =
          selectedOption.value === actualValue
            ? undefined
            : selectedOption.value;
        actualOnChange?.(newValue);
      }

      if (closeOnSelect) {
        setOpen(false);
      }      
      setSearchValue(""); // Limpa a busca ao selecionar
    },
    [actualValue, actualOnChange, actualOptions, closeOnSelect]
  );

  const handleOpenChange = React.useCallback((newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setSearchValue(""); // Limpa a busca ao fechar
    }
  }, []);

  const handleSearchChange = React.useCallback((value: string) => {
    setSearchValue(value);
  }, []);

  return (
    <Popover open={open} onOpenChange={handleOpenChange} modal={modal}>
      <PopoverTrigger className="text-[9pt] uppercasen" asChild>
        {trigger ? (
          <div ref={customTriggerRef} className="h-full">
            {triggerButton}
          </div>
        ) : (
          triggerButton
        )}
      </PopoverTrigger>
      <PopoverContent
        className="bg-transparent"
        style={{ width: triggerWidth }}
      >
        <Command className="max-h-[300px]" shouldFilter={false}>
          <CommandInput
            placeholder={searchPlaceholder}
            value={searchValue}
            onValueChange={handleSearchChange}
          />
          <CommandList className="max-h-[300px] overflow-y-auto">
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup>
              {listHeader ? (
                <div className="px-2 py-1 text-[11px] uppercase tracking-wide text-muted-foreground select-none">
                  {listHeader}
                </div>
              ) : null}
              {filteredOptions.map((option) => (
                <MemoizedCommandItem
                  key={String(option.value)}
                  option={option}
                  isSelected={actualValue === option.value}
                  onSelect={handleSelect}
                />
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// Componente memoizado para CommandItem
const MemoizedCommandItem = React.memo<{
  option: ComboboxItem<any>;
  isSelected: boolean;
  onSelect: (value: string) => void;
}>(({ option, isSelected, onSelect }) => (
  <CommandItem
    value={`${option.value} ${option.label}`}
    onSelect={onSelect}
    className="rounded-md px-2 py-1.5 cursor-pointer flex items-center gap-2"
  >
    {isSelected && (
      <div>
        <IconSquareCheckFilled className="size-5 text-primary" />
      </div>
    )}
    <span className="whitespace-pre-line">{option.label}</span>
  </CommandItem>
));

MemoizedCommandItem.displayName = "MemoizedCommandItem";
